import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, Clock, MapPin, Users, Download, ChevronRight,
  ExternalLink, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/events?upcoming_only=true`);
      const data = await response.json();
      setEvents(data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return { month: '', day: '' };
    const date = new Date(dateStr);
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      day: date.getDate()
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-gold-50/30">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-brand-navy to-navy-800">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.03%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-navy-300 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">Events</span>
          </nav>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-brand-gold/20 rounded-2xl flex items-center justify-center">
              <Calendar className="w-8 h-8 text-brand-gold" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
                Upcoming Events
              </h1>
              <p className="text-navy-200 mt-2">
                Join us at our upcoming meetings, workshops, and community events
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-green mx-auto mb-4" />
              <p className="text-navy-600">Loading events...</p>
            </div>
          ) : events.length > 0 ? (
            <div className="space-y-6">
              {events.map(event => {
                const { month, day } = formatShortDate(event.event_date);
                return (
                  <Card key={event.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="flex">
                      {/* Date Box */}
                      <div className="bg-brand-navy text-white p-6 flex flex-col items-center justify-center min-w-[100px]">
                        <span className="text-brand-gold text-sm font-bold">{month}</span>
                        <span className="text-4xl font-bold">{day}</span>
                      </div>
                      
                      {/* Event Details */}
                      <CardContent className="flex-1 p-6">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-navy-900 mb-2">{event.title}</h3>
                            {event.description && (
                              <p className="text-navy-600 mb-4">{event.description}</p>
                            )}
                            
                            <div className="flex flex-wrap gap-4 text-sm text-navy-500">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                {event.start_time}{event.end_time && ` - ${event.end_time}`}
                              </div>
                              
                              {event.location && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4" />
                                  {event.location}
                                  {event.location_address && `, ${event.location_address}`}
                                </div>
                              )}
                              
                              {event.is_virtual && (
                                <div className="flex items-center gap-2 text-brand-green">
                                  <ExternalLink className="w-4 h-4" />
                                  Virtual Event
                                </div>
                              )}
                              
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                {event.rsvp_count || 0} attending
                                {event.max_attendees && ` / ${event.max_attendees} spots`}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            <a
                              href={`${API_URL}/api/events/${event.id}/ical`}
                              className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-navy-200 rounded-lg hover:bg-navy-50 transition-colors"
                            >
                              <Download className="w-4 h-4" />
                              Add to Calendar
                            </a>
                            <Link to="/member-login">
                              <Button className="w-full bg-brand-green hover:bg-green-700">
                                Login to RSVP
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <Calendar className="w-16 h-16 text-navy-300 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-navy-900 mb-4">No Upcoming Events</h3>
              <p className="text-navy-500 max-w-md mx-auto mb-6">
                We don't have any events scheduled at the moment. Check back soon or join our newsletter to stay updated!
              </p>
              <div className="flex justify-center gap-4">
                <Link to="/contact">
                  <Button variant="outline">Subscribe to Newsletter</Button>
                </Link>
                <Link to="/join">
                  <Button className="bg-brand-green hover:bg-green-700">Become a Member</Button>
                </Link>
              </div>
            </Card>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gold-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-navy-900 mb-4">
            Want to RSVP and Access Member-Only Events?
          </h2>
          <p className="text-navy-600 mb-8">
            Join DC Men in ECE to get full access to all events, RSVP functionality, and exclusive member gatherings.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/join">
              <Button size="lg" className="bg-brand-green hover:bg-green-700">
                Become a Member
              </Button>
            </Link>
            <Link to="/member-login">
              <Button size="lg" variant="outline">
                Already a Member? Login
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
